import React, {
    createContext,
    useState,
    useContext,
    useEffect
} from 'react';

/* =======================
   TIPOS
======================= */

interface User {
    userId: string;
    username: string;
    email: string;
    role: 'admin' | 'user' | 'vendor';
    authProvider?: string;
    profilePhoto?: string;
    isVip?: boolean;
    vipExpiresAt?: Date | null;
    permissions?: {
        isEditor: boolean;
        isRevisor: boolean;
        isVendor: boolean;
        isApostilaRevisor: boolean;
    };
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isVendor: boolean;
    isVip: boolean;
    isEditor: boolean;
    isRevisor: boolean;
    isApostilaRevisor: boolean;
    loading: boolean;
    login: (accessToken: string, refreshToken: string, userData: User) => void;
    logout: (silent?: boolean) => void;
    checkAuth: () => Promise<void>;
    reloadProfile: () => Promise<void>;
    getAccessToken: () => string | null;
}

/* =======================
   CONTEXT
======================= */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* =======================
   PROVIDER
======================= */

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);


    /* ---------- OBTER ACCESS TOKEN ---------- */
    const getAccessToken = (): string | null => {
        return localStorage.getItem('accessToken');
    };

    /* ---------- REFRESH TOKEN AUTOMÁTICO ---------- */
    const refreshAccessToken = async (): Promise<string | null> => {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
            return null;
        }

        try {

            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken })
            });

            if (!response.ok) {
                throw new Error('Refresh token inválido');
            }

            const data = await response.json();

            // ✅ SALVAR NOVOS TOKENS
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);



            return data.accessToken;

        } catch (error) {
            console.error('❌ [REFRESH] Erro ao renovar token:', error);
            // Se falhar, deslogar silenciosamente (sessão já inválida no servidor)
            logout(true);
            return null;
        }
    };

    /* ---------- FETCH COM AUTO-REFRESH ---------- */
    const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
        let token = getAccessToken();


        if (!token) {
            throw new Error('Token não encontrado');
        }

        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };


        let response = await fetch(url, { ...options, headers });


        // ✅ VERIFICAR SE SESSÃO FOI REVOGADA
        if (response.status === 401) {
            console.log('🚨 [FETCH] Status 401 detectado!');

            const errorData = await response.json();
            console.log('📄 [FETCH] Dados do erro:', errorData);

            if (errorData.code === 'SESSION_REVOKED') {
                console.log('🚨 [AUTH] Sessão revogada - outro dispositivo logou');
                // ✅ logout silencioso: sessão já foi revogada no servidor
                logout(true);
                window.location.href = '/login';
                throw new Error('Sessão revogada');
            }

            if (errorData.code === 'TOKEN_EXPIRED') {
                console.log('🔄 [AUTH] Token expirado, tentando refresh...');

                // Tentar refresh
                const newToken = await refreshAccessToken();

                if (newToken) {
                    console.log('✅ [AUTH] Token renovado com sucesso, repetindo requisição...');
                    token = newToken;
                    const newHeaders = {
                        ...options.headers,
                        'Authorization': `Bearer ${token}`
                    };
                    response = await fetch(url, { ...options, headers: newHeaders });
                    console.log('📨 [AUTH] Nova resposta:', response.status);
                } else {
                    console.log('❌ [AUTH] Falha ao renovar token, fazendo logout');
                    logout(true);
                    throw new Error('Falha ao renovar token');
                }
            } else {
                console.log('❌ [AUTH] Erro 401 sem código conhecido:', errorData);
                throw new Error('Erro de autenticação');
            }
        }

        console.log('✅ [FETCH] Requisição concluída com sucesso');
        return response;
    };

    /* ---------- RECARREGAR PERFIL ---------- */
    const reloadProfile = async (): Promise<void> => {
        try {
            const profileResponse = await fetchWithAuth('/api/users/profile');

            if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                setUser(prev => prev ? {
                    ...prev,
                    profilePhoto: profileData.profilePhoto,
                    permissions: {
                        isEditor: profileData.editorConfig?.active === true,
                        isRevisor: profileData.revisorConfig?.active === true,
                        isVendor: profileData.vendorConfig?.enabled === true,
                        isApostilaRevisor: profileData.apostilaRevisorConfig?.active === true,
                    },
                } : null);
            }
        } catch (error) {
            console.error('Erro ao recarregar perfil:', error);
        }
    };

    /* ---------- LOGIN ---------- */
    const login = async (accessToken: string, refreshToken: string, userData: User) => {
        // ✅ SALVAR AMBOS OS TOKENS
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userId', userData.userId);
        localStorage.setItem('role', userData.role);

        // Definir usuário inicial
        setUser({
            userId: userData.userId,
            username: userData.username,
            email: userData.email ?? '',
            role: userData.role,
            profilePhoto: userData.profilePhoto,
            isVip: userData.isVip,
            vipExpiresAt: userData.vipExpiresAt
        });

        // ✅ Forçar recarregamento do perfil
        await reloadProfile();

        // ========== SINCRONIZAR CARRINHO APÓS LOGIN ==========
        try {
            const { cartService } = await import('../services/cartService');
            await cartService.syncCart();
            console.log('🛒 Carrinho sincronizado após login');
        } catch (cartError) {
            console.error('⚠️ Erro ao sincronizar carrinho:', cartError);
        }
    };

    /* ---------- LOGOUT ---------- */
    // silent = true → não chama API (sessão já foi revogada pelo servidor)
    // silent = false → chama API normalmente (logout voluntário do usuário)
    const logout = async (silent = false) => {
        if (!silent) {
            const token = getAccessToken();

            if (token) {
                try {
                    await fetch('/api/auth/logout', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                } catch (error) {
                    console.error('Erro ao fazer logout no servidor:', error);
                }
            }
        } else {
            console.log('🔕 [LOGOUT] Logout silencioso — sessão já revogada no servidor');
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('role');
        setUser(null);
    };

    /* ---------- CHECK AUTH ---------- */
    const checkAuth = async (): Promise<void> => {
        const token = getAccessToken();

        if (!token) {
            console.log('❌ Nenhum token encontrado');
            setLoading(false);
            return;
        }

        try {

            // Usar fetchWithAuth para auto-refresh
            const response = await fetchWithAuth('/api/auth/verify');

            if (!response.ok) {
                console.log('❌ Token inválido:', response.status);
                throw new Error('Token inválido');
            }

            const userData = await response.json();

            // ✅ Buscar perfil completo
            const profileResponse = await fetchWithAuth('/api/users/profile');

            let profilePhoto = null;
            let isVip = false;
            let vipExpiresAt = null;
            let permissions = { isEditor: false, isRevisor: false, isVendor: false, isApostilaRevisor: false };

            if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                profilePhoto = profileData.profilePhoto;

                if (profileData.vipAccess?.active) {
                    isVip = true;
                    vipExpiresAt = profileData.vipAccess.expiresAt;
                }

                permissions = {
                    isEditor: profileData.editorConfig?.active === true,
                    isRevisor: profileData.revisorConfig?.active === true,
                    isVendor: profileData.vendorConfig?.enabled === true,
                    isApostilaRevisor: profileData.apostilaRevisorConfig?.active === true,
                };
            }

            setUser({
                userId: userData.userId ?? userData._id,
                username: userData.username,
                email: userData.email ?? '',
                role: userData.role,
                profilePhoto,
                isVip,
                vipExpiresAt,
                permissions,
            });

        } catch (error) {
            console.error('Erro ao verificar autenticação:', error);
            // Logout silencioso no checkAuth — o servidor já sabe do estado
            logout(true);
        } finally {
            setLoading(false);
        }
    };

    /* ---------- EFFECT ---------- */
    useEffect(() => {
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------- CONTEXT VALUE ---------- */
    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isVendor: user?.role === 'vendor' || user?.permissions?.isVendor === true,
        isEditor: user?.permissions?.isEditor === true,
        isRevisor: user?.permissions?.isRevisor === true || user?.permissions?.isEditor === true,
        isApostilaRevisor: user?.permissions?.isApostilaRevisor === true || user?.permissions?.isEditor === true,
        isVip: user?.isVip === true,
        loading,
        login,
        logout,
        checkAuth,
        reloadProfile,
        getAccessToken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/* =======================
   HOOK
======================= */

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};